import { BusinessRole } from "@1games-ru/region-shared/dist/enums/business/role";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { CaseIcon, User2Icon } from "@/shared/icons";
import { BusinessSelectTab } from "@/domains/business/constants/businessSelectTab";
import {
  setMyBusiness,
  setSelectEmployeeBusinesses,
  setSelectOwnerBusinesses,
  setSelectSelectedTabs,
  setSelectTabs,
  setSelectTabsIcons,
} from "@/domains/business/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useLoadPlayerBusinesses() {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation("business");

  const [isPending, setIsPending] = useState(false);

  const loadBusinessesInfo = useCallback(async (): Promise<void> => {
    setIsPending(true);
    try {
      const response = await BusinessMpProvider.getMyBusinesses();

      if (response instanceof RpcMpProviderSuccessResponse) {
        const businessList = response?.data;
        const tabsList = [];
        const tabsIconsList = [];
        const myBusineses = [];
        const workPlaces = [];

        if (businessList?.length) {
          businessList.forEach((item) => {
            if (item.role === BusinessRole.Owner) {
              myBusineses.push(item);
            } else {
              workPlaces.push(item);
            }
          });
        }

        if (myBusineses.length) {
          tabsList.push(BusinessSelectTab.MYBUSINESS);
          tabsIconsList.push(CaseIcon);
        }
        if (workPlaces.length) {
          tabsList.push(BusinessSelectTab.WORKPLACES);
          tabsIconsList.push(User2Icon);
        }

        if (tabsList.length > 0) {
          dispatch(setSelectTabs(tabsList));
          dispatch(setSelectSelectedTabs(tabsList[0]));
          dispatch(setSelectTabsIcons(tabsIconsList));
        } else {
          dispatch(setSelectTabs(null));
          dispatch(setSelectSelectedTabs(null));
          dispatch(setSelectTabsIcons(null));
        }

        if (myBusineses.length > 0) {
          dispatch(setSelectOwnerBusinesses(myBusineses));
        } else {
          dispatch(setSelectOwnerBusinesses(null));
        }

        if (workPlaces.length > 0) {
          dispatch(setSelectEmployeeBusinesses(workPlaces));
        } else {
          dispatch(setSelectEmployeeBusinesses(null));
        }

        dispatch(setMyBusiness(businessList));
      } else if (
        response?.error?.message &&
        (i18n.exists(`business:business.errors.${response.error.message}`) ||
          i18n.exists(`business.errors.${response.error.message}`))
      ) {
        console.error(response.error.message);
        toast.error(t(`business.errors.${response.error.message}`));
      } else {
        console.error(response?.error?.message ?? "Unknown RPC error");
        toast.error(t("business.toast.infoLoadError"));
      }
    } catch (e) {
      console.error(e);
      toast.error(t("business.toast.infoLoadError"));
    } finally {
      setIsPending(false);
    }
  }, [dispatch, i18n, t]);

  return { isPending, loadBusinessesInfo };
}
